import { createAsyncThunk } from "@reduxjs/toolkit";
import { preLoginService, postLoginService } from "../../Services";
import { alert } from "../../utils/commonFunctions/alert";
import { scheduleTokenRefresh, storeTokens } from "../../utils/tokenRefresh";
import { setUserData } from "../../utils/auth";

const parseApiError = (error, fallbackMessage) => {
  const statusCode =
    error?.statusCode ||
    error?.status ||
    error?.response?.status ||
    500;

  const rawMessage =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.error?.message ||
    error?.error?.error ||
    error?.message ||
    fallbackMessage;

  const message = Array.isArray(rawMessage)
    ? rawMessage[0]
    : rawMessage || fallbackMessage;

  return {
    message,
    statusCode,
  };
};

const clearHybridSessionKeys = () => {
  sessionStorage.removeItem("v2_identifier");
  sessionStorage.removeItem("v2_identifier_type");
  sessionStorage.removeItem("v2_selected_role");
  sessionStorage.removeItem("v2_is_new_user");
};

export const sendHybridOTP = createAsyncThunk(
  "auth/sendHybridOTP",
  async (data, { rejectWithValue }) => {
    try {
      const response = await preLoginService.post("/api/auth/v2/send-otp", data);

      if (response?.success) {
        sessionStorage.setItem("v2_is_new_user", String(Boolean(response?.isNewUser)));
        return response;
      }

      return rejectWithValue({
        message: response?.message || "Failed to send OTP. Please try again.",
        statusCode: response?.statusCode || 400,
      });
    } catch (error) {
      const { message, statusCode } = parseApiError(
        error,
        "Failed to send OTP. Please try again."
      );

      if (statusCode >= 500 || message === "Network Error") {
        alert.error(
          message === "Network Error"
            ? "Unable to connect. Please check your internet connection."
            : message
        );
      }

      return rejectWithValue({ message, statusCode });
    }
  }
);

export const verifyHybridOTP = createAsyncThunk(
  "auth/verifyHybridOTP",
  async (data, { rejectWithValue }) => {
    try {
      const fcmToken = localStorage.getItem("fcmToken");
      const payload =
        fcmToken && !data?.fcmToken
          ? { ...data, fcmToken }
          : data;

      const response = await preLoginService.post("/api/auth/v2/verify-otp", payload);

      if (!response?.success || !response?.accessToken) {
        return rejectWithValue({
          message: response?.message || "Unable to verify OTP.",
          statusCode: response?.statusCode || 400,
        });
      }

      const role = response?.user?.role;
      const allowedRoles = ["merchant", "influencer"];
      if (!allowedRoles.includes(role)) {
        return rejectWithValue({
          message: "This account is not registered as a merchant or influencer.",
          statusCode: 403,
        });
      }

      storeTokens(response.accessToken, response.refreshToken);
      scheduleTokenRefresh(response.accessToken);
      setUserData(response.user || {});

      localStorage.setItem("profileComplete", String(Boolean(response?.profileComplete)));

      if (response?.user?.profile?.merchantId !== undefined && response?.user?.profile?.merchantId !== null) {
        localStorage.setItem("merchantId", String(response.user.profile.merchantId));
      } else {
        localStorage.removeItem("merchantId");
      }

      if (
        response?.user?.profile?.influencerId !== undefined &&
        response?.user?.profile?.influencerId !== null
      ) {
        localStorage.setItem("influencerId", String(response.user.profile.influencerId));
      } else {
        localStorage.removeItem("influencerId");
      }

      return response;
    } catch (error) {
      const { message, statusCode } = parseApiError(error, "Unable to verify OTP.");

      if (
        statusCode >= 500 ||
        message === "Network Error"
      ) {
        alert.error(
          message === "Network Error"
            ? "Unable to connect. Please check your internet connection."
            : message
        );
      }

      return rejectWithValue({ message, statusCode });
    }
  }
);

export const completeMerchantProfile = createAsyncThunk(
  "auth/completeMerchantProfile",
  async (data, { rejectWithValue }) => {
    try {
      const response = await postLoginService.put("/api/auth/v2/profile/merchant", data);

      if (!response?.success) {
        return rejectWithValue({
          message: response?.message || "Unable to complete merchant profile.",
          statusCode: response?.statusCode || 400,
        });
      }

      const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
      setUserData({
        ...currentUser,
        name: currentUser?.name || data?.businessName || currentUser?.name,
        email: data?.email || currentUser?.email,
        phone: data?.phone || currentUser?.phone,
      });
      localStorage.setItem("profileComplete", "true");

      clearHybridSessionKeys();
      window.dispatchEvent(new CustomEvent("user:updated"));

      return response;
    } catch (error) {
      const { message, statusCode } = parseApiError(
        error,
        "Unable to complete merchant profile."
      );
      return rejectWithValue({ message, statusCode });
    }
  }
);

export const completeInfluencerProfile = createAsyncThunk(
  "auth/completeInfluencerProfile",
  async (data, { rejectWithValue }) => {
    try {
      const response = await postLoginService.put("/api/auth/v2/profile/influencer", data);

      if (!response?.success) {
        return rejectWithValue({
          message: response?.message || "Unable to complete influencer profile.",
          statusCode: response?.statusCode || 400,
        });
      }

      const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
      setUserData({
        ...currentUser,
        name: currentUser?.name || data?.displayName || currentUser?.name,
        email: data?.email || currentUser?.email,
        phone: data?.phone || currentUser?.phone,
      });
      localStorage.setItem("profileComplete", "true");

      clearHybridSessionKeys();
      window.dispatchEvent(new CustomEvent("user:updated"));

      return response;
    } catch (error) {
      const { message, statusCode } = parseApiError(
        error,
        "Unable to complete influencer profile."
      );
      return rejectWithValue({ message, statusCode });
    }
  }
);

// Legacy auth flow thunks below are intentionally retained for rollback/reference.

export const signUp = createAsyncThunk("auth/signUp", async (data) => {
  const url = `/api/auth/signup`;
  try {
    // Always set role as merchant for this merchant site
    const payload = { ...data };
    const response = await preLoginService.post(url, payload);
    // console.log("SIGNUP RESPONSE---->", response);

    if (response?.success) {
      // Store tokens and user in localStorage with auto-refresh scheduling
      const accessToken = response.token || response.accessToken;
      const refreshToken = response.refreshToken;
      storeTokens(accessToken, refreshToken);
      localStorage.setItem("user", JSON.stringify(response.user));
      // alert.success("Account created successfully!");
      return response;
    } else {
      // Show specific error message from API response
      const errorMessage = response?.error?.message || response?.message || "Signup failed. Please try again.";
      throw { message: errorMessage };
    }
  } catch (error) {
    console.error("Signup error---->", error);
    // Handle network errors separately
    if (error?.message === "Network Error") {
      alert.error("Unable to connect. Please check your internet connection.");
      throw { message: "Network Error" };
    }

    // Show specific error message from API if available
    const errorMessage = error?.error?.error || error?.message || "Signup failed. Please try again.";
    alert.error(errorMessage);

    throw { message: errorMessage };
  }
});

export const login = createAsyncThunk("auth/login", async (data) => {
  const url = `/api/auth/login`;
  try {
    const response = await preLoginService.post(url, data);
    // console.log("LOGIN RESPONSE---->", response);

    // Only allow merchant role to login on this merchant site
    if (
      response?.success &&
      (response?.user?.role === "merchant" ||
        response?.user?.role === "influencer")
    ) {
      // Store tokens and user in localStorage with auto-refresh scheduling
      const accessToken = response.token || response.accessToken;
      const refreshToken = response.refreshToken;
      storeTokens(accessToken, refreshToken);
      localStorage.setItem("user", JSON.stringify(response.user));
      // alert.success("Sign in successfully");
      return response;
    } else {
      // Extract specific error message from API response
      const errorMessage =
        response?.error?.error ||
        response?.error?.message ||
        response?.message ||
        "Invalid credentials";
      alert.error(errorMessage);
      throw { message: errorMessage, error: response?.error };
    }
  } catch (error) {
    console.error("Login error---->", error);
    // Handle network errors separately
    if (error?.message === "Network Error") {
      alert.error("Unable to connect. Please check your internet connection.");
      throw { message: "Network Error" };
    }

    // Extract specific error message from API if available
    const errorMessage =
      error?.error?.error ||
      error?.error?.message ||
      error?.message ||
      "Invalid credentials";

    // Only show alert if not already shown
    if (!error?.message?.includes("Invalid credentials") && error?.message !== errorMessage) {
      alert.error(errorMessage);
    }

    throw { message: errorMessage, error: error?.error };
  }
});

export const merchantRegister = createAsyncThunk(
  "auth/merchantRegister",
  async (data) => {
    try {
      const url = `/business-users`;
      const response = await preLoginService.post(url, data);
      return response;
    } catch (error) {
      throw error.response?.data;
    }
  }
);

export const merchantPreference = createAsyncThunk(
  "auth/merchantPreference",
  async (data) => {
    try {
      const url = `/business-user-preferences`;
      const response = await preLoginService.post(url, data);
      return response;
    } catch (error) {
      throw error.response?.data;
    }
  }
);

export const getTotalMerchants = createAsyncThunk(
  "auth/getTotalMerchants",
  async () => {
    try {
      const url = `/business-users/count`;
      const response = await preLoginService.get(url);
      return response;
    } catch (error) {
      throw error.response?.data;
    }
  }
);

export const sendOTP = createAsyncThunk("auth/sendOTP", async (data) => {
  try {
    const url = `/api/auth/send-otp`;
    const response = await preLoginService.post(url, data);
    // console.log("sendOTP response---->", response);
    if (response?.success == true) {
      // alert.success("OTP sent successfully");
      return response;
    } else {
      // Show specific error message from API response
      const errorMessage = response?.error?.error || response?.message || "Failed to send OTP. Please try again.";
      alert.error(errorMessage);
      throw { message: errorMessage };
    }
  } catch (error) {
    console.error("sendOTP error---->", error);
    // Handle network errors separately
    if (error?.message === "Network Error") {
      alert.error("Unable to connect. Please check your internet connection.");
      throw { message: "Network Error" };
    }

    // Show specific error message from API if available
    const errorMessage = error?.error?.error || error?.message || "Failed to send OTP. Please try again.";

    // Only show toast if it hasn't been shown already
    if (!error?.message?.includes("An account with this email already exists")) {
      alert.error(errorMessage);
    }

    throw { message: errorMessage };
  }
});

export const verifyOTP = createAsyncThunk("auth/verifyOTP", async (data) => {
  try {
    const url = `/api/auth/verify-otp`;
    const response = await preLoginService.post(url, data);
    if (response?.success == true) {
      // alert.success("OTP verified successfully!");
      return response;
    } else {
      // Always show custom message for OTP verification failure
      alert.error("Invalid OTP. Please try again.");
      throw { message: "Invalid OTP. Please try again." };
    }
  } catch (error) {
    // Handle network errors separately
    if (error?.message === "Network Error") {
      alert.error("Unable to connect. Please check your internet connection.");
    } else if (!error?.message?.includes("Invalid OTP")) {
      alert.error("Invalid OTP. Please try again.");
    }
    throw { message: "Invalid OTP. Please try again." };
  }
});

export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async (data) => {
    try {
      const url = `/api/auth/forgot-password`;
      const response = await preLoginService.post(url, data);
      if (response?.success) {
        // alert.success(
        //   response?.message || "Password reset link sent to your email"
        // );
        return response;
      } else {
        alert.error(response?.message || "Failed to send password reset link");
        throw {
          message: response?.message || "Failed to send password reset link",
        };
      }
    } catch (error) {
      const errorMessage =
        error?.message || "Failed to send password reset link";
      alert.error(errorMessage);
      throw error;
    }
  }
);

export const refreshToken = createAsyncThunk(
  "auth/refreshToken",
  async (_, { rejectWithValue }) => {
    try {
      const refreshToken = localStorage.getItem("refreshToken");
      const accessToken = localStorage.getItem("token");
      // console.log("207---->REFRESH TOKEN PAYLOAD---->", { refreshToken });
      // console.log("208---->ACCESS TOKEN PAYLOAD---->", { accessToken });

      const refreshPayload = { refreshToken, accessToken };
      if (!refreshToken) {
        throw new Error("No refresh token available");
      }

      const url = `/api/auth/refresh-token`;
      const response = await preLoginService.post(url, refreshPayload);

      if (response?.success) {
        // Update tokens in localStorage
        localStorage.setItem("token", response.accessToken);
        localStorage.setItem("refreshToken", response.refreshToken);
        return response;
      } else {
        throw new Error(response?.message || "Token refresh failed");
      }
    } catch (error) {
      const errorMessage = error?.message || "Token refresh failed";
      return rejectWithValue(errorMessage);
    }
  }
);

export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async (data) => {
    try {
      const { token, newPassword } = data;
      const url = `/api/auth/reset-password?token=${token}`;

      // Pass token in Authorization header and only newPassword in body
      const response = await preLoginService.post(
        url,
        { newPassword },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response?.success) {
        // alert.success(response?.message || "Password reset successfully!");
        return response;
      } else {
        alert.error(response?.message || "Failed to reset password");
        throw {
          message: response?.message || "Failed to reset password",
        };
      }
    } catch (error) {
      const errorMessage = error?.message || "Failed to reset password";
      alert.error(errorMessage);
      throw error;
    }
  }
);

export const sendLoginOTP = createAsyncThunk(
  "auth/sendLoginOTP",
  async (data) => {
    try {
      const url = `/api/auth/send-login-otp`;
      const response = await preLoginService.post(url, data);
      // console.log("sendLoginOTP response---->", response);

      if (response?.success === true) {
        // alert.success("OTP sent successfully");
        return response;
      } else {
        // Show specific error message from API response
        const errorMessage =
          response?.error?.error ||
          response?.error?.message ||
          response?.message ||
          "Failed to send OTP. Please try again.";
        alert.error(errorMessage);
        throw { message: errorMessage };
      }
    } catch (error) {
      console.error("sendLoginOTP error---->", error);

      // Handle network errors separately
      if (error?.message === "Network Error") {
        alert.error("Unable to connect. Please check your internet connection.");
        throw { message: "Network Error" };
      }

      // Show specific error message from API if available
      const errorMessage =
        error?.error?.error ||
        error?.message ||
        "Failed to send OTP. Please try again.";

      // Don't show toast again if already shown
      if (!error?.message?.includes("No account found") &&
        !error?.message?.includes("cannot be used")) {
        alert.error(errorMessage);
      }

      throw { message: errorMessage };
    }
  }
);

export const verifyLoginOTP = createAsyncThunk(
  "auth/verifyLoginOTP",
  async (data) => {
    try {
      const url = `/api/auth/verify-login-otp`;
      const response = await preLoginService.post(url, data);
      // console.log("verifyLoginOTP response---->", response);

      if (response?.success === true) {
        // Only allow merchant and influencer roles to login on this portal
        if (
          response?.user?.role === "merchant" ||
          response?.user?.role === "influencer"
        ) {
          // Store tokens and user in localStorage with auto-refresh scheduling
          const accessToken = response.token || response.accessToken;
          const refreshToken = response.refreshToken;
          storeTokens(accessToken, refreshToken);
          localStorage.setItem("user", JSON.stringify(response.user));
          // alert.success("Sign in successfully");
          return response;
        } else {
          // Not a merchant/influencer - reject login
          alert.error("This account cannot be used to sign in here.");
          throw { message: "This account cannot be used to sign in here." };
        }
      } else {
        // Always show custom message for OTP verification failure
        alert.error("Invalid OTP. Please try again.");
        throw { message: "Invalid OTP. Please try again." };
      }
    } catch (error) {
      console.error("verifyLoginOTP error---->", error);

      // Handle network errors separately
      if (error?.message === "Network Error") {
        alert.error("Unable to connect. Please check your internet connection.");
        throw { message: "Network Error" };
      }

      // Show specific error messages or generic OTP error
      const errorMessage =
        error?.error?.error ||
        error?.message ||
        "Invalid OTP. Please try again.";

      if (!error?.message?.includes("Invalid OTP") &&
        !error?.message?.includes("cannot be used")) {
        alert.error(errorMessage);
      }

      throw { message: errorMessage };
    }
  }
);

export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async (_, { rejectWithValue }) => {
    try {
      const accessToken = localStorage.getItem("token");
      const refreshToken = localStorage.getItem("refreshToken");

      const payload = {
        accessToken: accessToken || "",
        refreshToken: refreshToken || "",
      };

      // FCM token is optional — only for mobile container apps
      const fcmToken = localStorage.getItem("fcmToken");
      if (fcmToken) {
        payload.fcmToken = fcmToken;
      }

      const response = await postLoginService.post("/api/auth/logout", payload);
      return response;
    } catch (error) {
      console.error("Logout API error:", error);
      // Return the error but don't block — we'll still clean up locally
      return rejectWithValue(error?.response?.data || error?.message || "Logout API failed");
    }
  }
);
